import { NavLink } from "react-router-dom";
import { useObjectTypes } from "@/hooks/useObjectTypes";
import { useCaseStore } from "@/stores/caseStore";
import { ObjectTypeIcon } from "@/components/ObjectTypeIcon";
import {
  Briefcase,
  CalendarDays,
  GitBranch,
  MapPin,
  Search,
  Settings,
  Tags,
  Link2,
  Sparkles,
  PanelLeftClose,
  PanelLeft,
} from "lucide-react";

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
    isActive
      ? "bg-accent-100 text-accent-700 dark:bg-neutral-800 dark:text-accent-400"
      : "text-text-secondary hover:bg-neutral-100 hover:text-text-primary dark:hover:bg-neutral-800"
  }`;

export function Sidebar({ collapsed, onToggleCollapsed }: SidebarProps) {
  const activeCaseId = useCaseStore((s) => s.activeCaseId);
  const { data: objectTypes } = useObjectTypes(activeCaseId);

  return (
    <aside
      className={`flex h-full flex-col border-r border-border bg-surface transition-all ${
        collapsed ? "w-14" : "w-60"
      }`}
    >
      <div className="flex items-center justify-between border-b border-border px-3 py-3">
        {!collapsed && (
          <span className="text-lg font-bold tracking-tight">Athena</span>
        )}
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="rounded p-1 text-text-secondary hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          {collapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto p-2 space-y-6">
        <div>
          {!collapsed && (
            <p className="mb-1 px-3 text-xs font-semibold uppercase text-text-muted">
              Investigations
            </p>
          )}
          <div className="space-y-0.5">
            <NavLink to="/cases" className={navLinkClass} title={collapsed ? "Cases" : undefined}>
              <Briefcase size={18} />
              {!collapsed && <span>Cases</span>}
            </NavLink>
          </div>
        </div>

        <div>
          {!collapsed && (
            <p className="mb-1 px-3 text-xs font-semibold uppercase text-text-muted">
              Browse
            </p>
          )}
          <div className="space-y-0.5">
            {objectTypes?.map((ot) => (
              <NavLink
                key={ot.id}
                to={`/types/${ot.id}`}
                className={navLinkClass}
                title={collapsed ? ot.pluralName : undefined}
              >
                <ObjectTypeIcon icon={ot.icon} color={ot.color} size={18} />
                {!collapsed && (
                  <>
                    <span className="flex-1 truncate">{ot.pluralName}</span>
                    <span className="text-xs text-text-muted">
                      {ot.instanceCount}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </div>

        <div>
          {!collapsed && (
            <p className="mb-1 px-3 text-xs font-semibold uppercase text-text-muted">
              Tools
            </p>
          )}
          <div className="space-y-0.5">
            <NavLink to="/graph" className={navLinkClass} title={collapsed ? "Graph" : undefined}>
              <GitBranch size={18} />
              {!collapsed && <span>Graph</span>}
            </NavLink>
            <NavLink to="/map" className={navLinkClass} title={collapsed ? "Map" : undefined}>
              <MapPin size={18} />
              {!collapsed && <span>Map</span>}
            </NavLink>
            <NavLink to="/timeline" className={navLinkClass} title={collapsed ? "Timeline" : undefined}>
              <CalendarDays size={18} />
              {!collapsed && <span>Timeline</span>}
            </NavLink>
            <NavLink to="/search" className={navLinkClass} title={collapsed ? "Search" : undefined}>
              <Search size={18} />
              {!collapsed && <span>Search</span>}
            </NavLink>
          </div>
        </div>

        <div>
          {!collapsed && (
            <p className="mb-1 px-3 text-xs font-semibold uppercase text-text-muted">
              Admin
            </p>
          )}
          <div className="space-y-0.5">
            <NavLink to="/types" end className={navLinkClass} title={collapsed ? "Object Types" : undefined}>
              <Settings size={18} />
              {!collapsed && <span>Object Types</span>}
            </NavLink>
            <NavLink to="/admin/relationship-types" className={navLinkClass} title={collapsed ? "Relationships" : undefined}>
              <Link2 size={18} />
              {!collapsed && <span>Relationship Types</span>}
            </NavLink>
            <NavLink to="/admin/tags" className={navLinkClass} title={collapsed ? "Tags" : undefined}>
              <Tags size={18} />
              {!collapsed && <span>Tags</span>}
            </NavLink>
            <NavLink to="/admin/enrichment" className={navLinkClass} title={collapsed ? "Enrichment" : undefined}>
              <Sparkles size={18} />
              {!collapsed && <span>Enrichment</span>}
            </NavLink>
          </div>
        </div>
      </nav>
    </aside>
  );
}
